import { Link } from "react-router-dom";
import { hotRank, news } from "@/mocks/content";
import { formatCount } from "@/utils/format";
import { CategoryPill, MetaLine } from "@/components/feature/Cards";

const byId = new Map(news.map((n) => [n.id, n]));
const hotItems = hotRank.map((id) => byId.get(id)!).filter(Boolean);

function DarkHotPanel() {
  return (
    <aside className="relative overflow-hidden rounded-2xl bg-foreground-950 p-6 text-background-100">
      <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary-600/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-12 -left-8 h-36 w-36 rounded-full bg-accent-500/20 blur-3xl" />
      <div className="relative">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold">
            <i className="ri-fire-fill text-accent-400" />
            吃瓜热榜
          </h2>
          <Link
            to="/category/trending"
            className="text-xs text-background-300 transition-colors hover:text-accent-300"
          >
            全部热瓜
          </Link>
        </div>
        <ol className="mt-4 space-y-1.5">
          {hotItems.slice(0, 6).map((item, i) => (
            <li key={item.id}>
              <Link
                to={`/news/${item.id}`}
                className="group flex items-start gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-background-100/10"
              >
                <span
                  className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-heading text-xs font-bold ${
                    i < 3
                      ? "bg-accent-500 text-foreground-900"
                      : "bg-background-100/15 text-background-300"
                  }`}
                >
                  {i + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="line-clamp-2 text-sm font-medium leading-snug text-background-100 transition-colors group-hover:text-accent-200">
                    {item.title}
                  </span>
                  <span className="mt-0.5 flex items-center gap-2 text-xs text-background-300/80">
                    <i className="ri-eye-line" />
                    {formatCount(item.views)} 阅读
                    <span className="text-accent-400">{item.time}</span>
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ol>
      </div>
    </aside>
  );
}

export default function Hero() {
  const lead = hotItems[0];
  const subs = hotItems.slice(1, 3);
  return (
    <section className="mx-auto max-w-7xl px-4 pb-10 pt-6 md:px-6 md:pt-8">
      <div className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-background-200 pb-4">
        <span className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-2.5 py-1 text-xs font-bold text-background-50">
          <i className="ri-newspaper-fill" /> 今日头条
        </span>
        <span className="text-xs text-foreground-600">
          全网独家内幕 · 爆料深度栏目持续更新中
        </span>
        <span className="ml-auto hidden text-xs text-foreground-400 sm:block">
          每天 10:00 / 20:00 两场瓜讯
        </span>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="flex flex-col gap-6 lg:col-span-2">
          {/* H1 头条 */}
          <article className="group overflow-hidden rounded-2xl border border-background-200 bg-background-50">
            <Link to={`/news/${lead.id}`} className="block">
              <div className="relative h-56 overflow-hidden md:h-80">
                <img
                  src={lead.cover}
                  alt={`${lead.title} 独家头条报道配图`}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent" />
                {lead.exclusive && (
                  <span className="absolute left-4 top-4 inline-flex items-center gap-1 rounded-full bg-primary-600 px-3 py-1 text-xs font-semibold text-background-50">
                    <i className="ri-flashlight-fill" /> 独家
                  </span>
                )}
              </div>
              <div className="p-5 md:p-6">
                <CategoryPill slug={lead.category} />
                <h1 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700 md:text-3xl">
                  {lead.title}
                </h1>
                <p className="mt-3 hidden max-w-3xl text-sm leading-relaxed text-foreground-600 sm:line-clamp-2">
                  {lead.lead}
                </p>
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                  <MetaLine item={lead} />
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary-600">
                    查看独家详情
                    <i className="ri-arrow-right-line" />
                  </span>
                </div>
              </div>
            </Link>
          </article>

          {/* 次头条 */}
          <div className="grid gap-4 sm:grid-cols-2">
            {subs.map((item) => (
              <Link
                key={item.id}
                to={`/news/${item.id}`}
                className="group flex items-center gap-3 rounded-xl border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-200"
              >
                <div className="relative h-20 w-24 shrink-0 overflow-hidden rounded-lg">
                  <img
                    src={item.cover}
                    alt={`${item.title} 次条配图`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="min-w-0">
                  <h3 className="line-clamp-2 text-sm font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
                    {item.title}
                  </h3>
                  <MetaLine item={item} className="mt-2" />
                </div>
              </Link>
            ))}
          </div>
        </div>

        <DarkHotPanel />
      </div>
    </section>
  );
}